import {AddSchema, CreateAccessor, GetDoc, GetDocs, MGLClass} from "mobx-graphlink";
import {emptyArray} from "js-vextensions";
import {WorldVisit} from "../../../EchoHunt/Types.js";
export const GetEchoStory = CreateAccessor((id: string|n): EchoStory|n=>{
	if (id == null) return null;
	return GetDoc({}, a=>a.echoStories.get(id));
});
export const GetEchoStories = CreateAccessor((userID: string|n): EchoStory[]=>{
	if (userID == null) return emptyArray; // if we aren't signed-in, don't even try to make request (the security-rules will make it fail)
	return GetDocs({params: {filter: {creator: userID && {equalTo: userID}}}}, a=>a.echoStories);
});

/** One row per character run (§14 `echo_stories`). A story spans several rounds and survives across
 * them: each round reads `summary` and writes back `summaryOut` (§8.3, P11). */
@MGLClass({table: "echoStories"})
export class EchoStory {
	constructor(data?: Partial<EchoStory>) { Object.assign(this, data); }

	id: string;
	creator: string;
	createdAt: number;

	/** the pool character this story follows (§8.7). An ID, not a snapshot: editing the character is meant to
	 * change the running story. Absent/null = none assigned or deleted, which blocks play until P11's picker. */
	characterId?: string|n;

	/** running storyline summary carried between rounds (§8.1, §8.3) */
	summary?: string|n;

	/** 0..1; the meter as the STORY derived it. Only `ApplySuspicionAfterRound_Live` moves this, so it is always
	 * an honest record of how often the user found their own echoes. Past a threshold the character turns lucid
	 * (§8.4) -- but the threshold is compared against `suspicionAccumulated + suspicionUserAdjustment`. */
	suspicionAccumulated?: number|n;

	/** A SIGNED offset the user applied by hand (can be negative). The meter shown is
	 * `clamp(suspicionAccumulated + suspicionUserAdjustment)`. An offset rather than an overwrite, so the
	 * story-derived value is never destroyed -- resetting to it is just setting this back to 0. */
	suspicionUserAdjustment?: number|n;

	/** P11: requests that the NEXT installment offer a way out, and is cleared once one has been woven. Kept on
	 * the story (not passed per-call) because P13 pre-generates rounds, when there is no caller to pass it. */
	doorOverrideRequested?: boolean|n;

	/** worlds this story has visited, in order (§14).
	 * §11.5 W1: this is the SINGLE SOURCE OF TRUTH for where the story is -- `worldId` was removed because a
	 * second copy of the location drifted from this one. Every move is exactly one append. */
	worldHistory?: WorldVisit[]|n;

	/** set when the character has become lucid / woken, ending the run (§8.3) */
	ended?: boolean|n;
	/** the narrated interlude shown when suspicion boiled over (§8.4) */
	lucidInterlude?: string|n;
}
AddSchema("EchoStory", {
	properties: {
		id: {type: "string"},
		creator: {type: "string"},
		createdAt: {type: "number"},

		characterId: {type: ["null", "string"]},

		summary: {type: ["null", "string"]},

		suspicionAccumulated: {type: ["null", "number"]},

		suspicionUserAdjustment: {type: ["null", "number"]},

		doorOverrideRequested: {type: ["null", "boolean"]},

		worldHistory: {oneOf: [{type: "null"}, {items: {$ref: "WorldVisit"}}]},

		ended: {type: ["null", "boolean"]},
		lucidInterlude: {type: ["null", "string"]},
	},
});
